<template>
    <div class="form-item-text-input">
        <div class="d-flex flex-row">
            <label v-if="enumeration != ''" class="text-color-number mb-1">
                {{ enumeration }}
            </label>
            <label v-if="label" :for="name" :class="labelClass"
                class=" mb-1 ps-2">
                {{ label }}
                <span v-if="required == 1" class="required">*</span>
            </label>
        </div>
        <div v-if="helpText" class="form-input-help mb-1">{{ helpText }}</div>
        <input
            :id="fieldId"
            v-model="value"
            :type="type"
            :class="[inputClass, { 'is-invalid': shouldShowError }]"
            :placeholder="textPlaceholder"
            :name="name" />

        <div v-if="shouldShowError" class="has-error-msg invalid-feedback d-block">
            {{ errorMessage }}
        </div>
    </div>
</template>

<script setup>
import { computed, watch } from 'vue';
import { useField } from 'vee-validate';
import { useTranslation } from '@/composables/useTranslation';

// INITIALIZE STORE & COMPOSABLES
const { translate } = useTranslation();

// PROPS & EMITS
const props = defineProps({
    enumeration: {
        type: String,
        required: false,
        default: '',
    },
    errorText: {
        type: String,
        required: false,
    },
    fieldId: {
        type: Number,
        required: true,
    },
    helpText: {
        type: String,
        required: false,
    },
    inputClass: {
        type: String,
        required: false,
    },
    label: {
        type: String,
        required: true,
    },
    labelClass: {
        type: String,
        required: false,
    },
    modelAnswer: {
        type: String,
        required: false,
    },
    name: {
        type: String,
        required: true,
    },
    required: {
        type: Number,
        required: false,
    },
    placeholder: {
        type: Number,
        required: false,
    },
    type: {
        type: String,
        required: true,
    },
    validation: {
        type: Object,
        required: false,
    },
    hasSubmitted: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:modelAnswer']);

// REACTIVE VARIABLES

// COMPUTED PROPERTIES
const textPlaceholder = computed(() => {
    if (props.placeholder == true || props.placeholder == 1) {
        return props.label;
    }
    return '';
});
const validationRules = computed(() => {
    return (value) => {
        if (props.validation.required && (!value || value.toString().trim() === '')) {
            return props.errorText || translate('label.this_value_is_required');
        }

        if (props.validation.email && value) {
            const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            if (!emailPattern.test(value)) {
                return translate('label.email_invalid');
            }
        }

        return true;
    };
});

// Solo mostrar errores después del primer submit
const shouldShowError = computed(() => {
    return props.hasSubmitted && errorMessage.value;
});

// METHODS
// Configurar useField con validación lazy
const { value, errorMessage, meta } = useField(props.name, validationRules, {
    initialValue: props.modelAnswer,
    validateOnValueUpdate: false, // No validar en cada cambio
    validateOnMount: false,       // No validar al montar
});

// LIFECYCLE HOOKS
// Sincronizar con el valor externo
watch(() => props.modelAnswer, (newValue) => {
    value.value = newValue;
});

// Emitir cambios al componente padre
watch(value, (newValue) => {
    emit('update:modelAnswer', newValue);
});
</script>
